import { FEATURE_FLAG_DEFINITIONS } from '../app-configuration/customization/feature-flag-definitions.js';
import { OPTION_DEFINITIONS } from '../app-configuration/customization/option-definitions.js';

/**
 * @import { FeatureFlagType } from '../app-configuration/customization/feature-flag-definitions.js';
 * @import { FeatureFlagDefinition } from '../app-configuration/customization/feature-flag-definitions.js';
 * @import { OptionType } from '../app-configuration/customization/option-definitions.js';
 * @import { OptionDefinition } from '../app-configuration/customization/option-definitions.js';
 */

/**
 * Get the customization definitions for a given entity type and its current customization keys.
 * @template {FeatureFlagDefinition|OptionDefinition} T
 * @param {FeatureFlagType|OptionType} entityType - The entity type to get customization definitions for
 * @param {string[]} entityCustomizationKeys - The keys for the customizations the entity currently has
 * @param {T[]} allCustomizationDefinitions - The definitions to pull from
 * @param {Object} [options]
 * @param {boolean} [options.includeAllPrivate] - Whether to include all private customizations (should only be used in admin contexts)
 * @returns {T[]} - Returns an array of customization definitions that are applicable to the entity type and keys provided.
 */
function getCustomizationDefinitions(entityType, entityCustomizationKeys, allCustomizationDefinitions, options = {}) {
  const { includeAllPrivate = false } = options;

  // get the list of customization definitions that are available based on the type
  const properlyTypedCustomizations = allCustomizationDefinitions.filter(
    (customizationDefinition) => customizationDefinition.type === entityType
  );

  // get the list of public and private definitions
  const publicCustomizationDefinitions = properlyTypedCustomizations.filter(
    (featureFlagDefinition) => featureFlagDefinition.private !== true
  );
  const privateCustomizationDefinitions = properlyTypedCustomizations.filter(
    (featureFlagDefinition) => featureFlagDefinition.private === true
  );

  // get customization definitions for the entity to compute the private definitions that should be included
  const visiblePrivateCustomizationsDefinitions = privateCustomizationDefinitions.filter((customizationDefinition) => {
    const name = customizationDefinition.name;
    return includeAllPrivate || entityCustomizationKeys.includes(name);
  });

  // combine the public and visible private definitions for the entity
  return [...publicCustomizationDefinitions, ...visiblePrivateCustomizationsDefinitions];
}

/**
 * Get the feature flag definitions for a given entity type and its current feature flag names.
 * @param {FeatureFlagType} entityType - The entity type to get customization definitions for
 * @param {string[]} [entityFeatureFlagNames] - The feature flags name for the current entity
 * @param {Object} [options]
 * @param {boolean} [options.includeAllPrivate] - Whether to include all private feature flags (should only be used in admin contexts)
 * @returns {FeatureFlagDefinition[]} - Returns an array of feature flag definitions that are applicable to the entity type and keys provided.
 */
export function GetFlagDefinitionsForEntity(entityType, entityFeatureFlagNames = [], options = {}) {
  const flagDefinitions = getCustomizationDefinitions(entityType, entityFeatureFlagNames, FEATURE_FLAG_DEFINITIONS, options);
  return flagDefinitions;
}

/**
 * Get the option definitions for a given entity type and its current option keys.
 * @param {OptionType} entityType - The entity type to get customization definitions for
 * @param {string[]} [entityOptionKeys] - The options for the entity
 * @param {Object} [options]
 * @param {boolean} [options.includeAllPrivate] - Whether to include all private options (should only be used in admin contexts)
 * @return {OptionDefinition[]} - Returns an array of option definitions that are applicable to the entity type and keys provided.
 */
export function GetOptionDefinitionsForEntity(entityType, entityOptionKeys = [], options = {}) {
  const optionDefinitions = getCustomizationDefinitions(entityType, entityOptionKeys, OPTION_DEFINITIONS, options);
  return optionDefinitions;
}

/**
 * Gets the flag definition for a given feature flag.  This is used with server-side code
 * @param {string} featureFlagName - The name of the feature flag to get the definition for
 * @returns {FeatureFlagDefinition|undefined} - The definition for the feature flag
 */
export function GetFeatureFlagDefinition(featureFlagName) {
  return FEATURE_FLAG_DEFINITIONS.find((flagDefinition) => flagDefinition.name == featureFlagName);
}
